<script setup lang="ts">
/**
 * BetSlipDrawer
 *
 * Full-screen drawer for displaying BetSlip content via deep links.
 * Includes share functionality to open ShareBetSlipDialog with the active conid or productConid.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import BetSlipByConid from './BetSlip/BetSlipByConid.vue';
import BetSlipByProduct from './BetSlip/BetSlipByProduct.vue';
import ShareBetSlipDialog from './ShareBetSlipDialog.vue';
import { determineBetSlipMode } from './BetSlip/utils';
import type { BetSlipItem } from '@/types/betslip.types';
import { useUserState } from '@/stores/useUserState';

const { t } = useI18n();
const userState = useUserState();
//todo move these to seprarate composable
function login() {
  const domain = window.location.host.replace(/^[^.]+\./g, '');
  window.location.href = `https://www.${domain}/sso/Login?forwardTo=100`;
}

function signup() {
  const domain = window.location.host.replace(/^[^.]+\./g, '');
  window.location.href = `https://www.${domain}/Universal/Application?eventContract=T`;
}

const props = defineProps<{
  /** Whether the drawer is visible */
  visible: boolean;
  /** Single contract ID (takes precedence over productConid) */
  conid?: number;
  /** Election product ID */
  productConid?: number;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Emitted when header is clicked */
  'header-click': [];
  /** Emitted when an item is clicked */
  'item-click': [item: BetSlipItem];
}>();

// Determine which mode we're in (conid takes precedence)
const mode = computed(() => determineBetSlipMode(props.conid, props.productConid));

const shareDialogVisible = ref(false);

// PrimeVue Drawer pass-through
const DRAWER_PT = {
  header: 'flex justify-end',
  pcCloseButton: { root: '!visible !hidden' },
  content: '!p-0',
} as const;

function handleHeaderClick() {
  emit('header-click');
}

function handleItemClick(item: BetSlipItem) {
  emit('item-click', item);
}
</script>

<template>
  <Drawer
    :visible="visible"
    position="right"
    :closable="false"
    :pt="DRAWER_PT"
    class="betslip-drawer-fullscreen"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="ratio-design-provider h-full flex flex-col">
      <!-- Content area -->
      <div class="flex-1 px-4 pb-4 overflow-auto">
        <!-- Error state when no params provided -->
        <div v-if="mode === 'none'" class="flex flex-col items-center justify-center h-full text-center p-8">
          <div class="i-mdi-alert-circle text-4xl text-error mb-4" />
          <p class="text-on-surface-variant">{{ t('invalidBetSlipParameters') }}</p>
        </div>

        <!-- BetSlip in rounded container with shadow -->
        <div v-else class="bg-surface rounded-xl shadow-md overflow-hidden">
          <BetSlipByConid
            v-if="mode === 'conid'"
            :conid="conid!"
            @header-click="handleHeaderClick"
            @item-click="handleItemClick"
          />
          <BetSlipByProduct
            v-else-if="mode === 'product'"
            :product-conid="productConid!"
            @header-click="handleHeaderClick"
            @item-click="handleItemClick"
          />
        </div>
        <nav
          :aria-label="t('electionBoardActions')"
          class="flex flex-col sm:flex-wrap sm:flex-row sm:justify-end gap-2 mt-4"
        >
          <Button
            v-if="mode !== 'none'"
            :label="t('share')"
            icon="ibi-share-send"
            severity="primary"
            :aria-label="t('shareThisContract')"
            @click="shareDialogVisible = true"
          />
          <Button
            :label="t('viewElectionBoard')"
            severity="primary"
            :aria-label="t('closeDrawerViewElectionBoard')"
            @click="emit('update:visible', false)"
          />
          <template v-if="!userState.authenticated">
            <Button
              :label="t('share_log_in')"
              severity="primary"
              size="small"
              :aria-label="t('logInToAccount')"
              @click="login"
            />
            <Button
              :label="t('open_account')"
              severity="primary"
              size="small"
              :aria-label="t('openNewAccount')"
              :pt="{ label: { 'data-type': 'cta' } }"
              @click="signup"
            />
          </template>
        </nav>
      </div>
    </div>
  </Drawer>

  <ShareBetSlipDialog
    :key="`share-${conid}-${productConid}`"
    v-model:visible="shareDialogVisible"
    :conid="conid"
    :product-conid="productConid"
  />
</template>

<style>
/* Global style needed since Drawer portals to body */
.betslip-drawer-fullscreen.p-drawer {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw !important;
}
</style>
<!-- last modified by AI tools on 2026-10-07 -->
